import map from "lodash/map";
import { useFormSkillsFrameworksSuggestions } from "PFApp/activities/activity_form/hooks/use_form_skills_frameworks_suggestions";
import { FIELD_SKILLS_FRAMEWORK_IDS_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { getUnselectedSkillsFrameworksSuggestions } from "PFApp/helpers/activity_form/get_unselected_skills_frameworks_suggestions";
import { Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { SkillsFramework, SkillsFrameworkRank } from "PFCore/types";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { useFormSelectedSkillsFrameworks } from "../../../hooks/use_form_selected_skills_frameworks";
import { FormSection, SectionProps } from "../../form_section";
import { useSkillsFrameworksWarningAlert } from "./hooks/use_skills_frameworks_warning_alert";
import { SkillsFrameworksEmptyState } from "./parts/skills_frameworks_empty_state";
import { SkillsFrameworksList } from "./parts/skills_frameworks_list";
import { SkillsFrameworksSectionActions } from "./parts/skills_frameworks_section_actions";
import { SkillsFrameworksSuggestionsBox } from "./parts/skills_frameworks_suggestions_box";

export const SkillsFrameworksFormSection = ({ id, title }: SectionProps) => {
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [isConfigMatchFilterOn, setIsConfigMatchFilterOn] = useState(true);
  const [initiallyPreselectedSkillsFramework, setInitiallyPreselectedSkillsFramework] =
    useState<SkillsFramework>();

  const { selectedSkillsFrameworks, isSkillsFrameworksLoading } = useFormSelectedSkillsFrameworks();
  const {
    primarySkillsFrameworksSuggestions,
    secondarySkillsFrameworksSuggestions,
    isSkillsFrameworksSuggestionsFetching
  } = useFormSkillsFrameworksSuggestions(isConfigMatchFilterOn);
  const { resetWarningEffect } = useSkillsFrameworksWarningAlert();
  const { setValue } = useFormContext();

  const hasSkillsFrameworks = selectedSkillsFrameworks.length > 0;
  const showEmptyState = !isSkillsFrameworksLoading && !hasSkillsFrameworks;
  const selectedPrimaryFrameworks = selectedSkillsFrameworks.filter(
    ({ rank }) => SkillsFrameworkRank.Primary === rank
  );
  const selectedSecondaryFrameworks = selectedSkillsFrameworks.filter(
    ({ rank }) => SkillsFrameworkRank.Secondary === rank
  );
  const primaryFrameworksSuggestions = getUnselectedSkillsFrameworksSuggestions(
    primarySkillsFrameworksSuggestions,
    selectedPrimaryFrameworks
  );
  const secondaryFrameworksSuggestions = getUnselectedSkillsFrameworksSuggestions(
    secondarySkillsFrameworksSuggestions,
    selectedSecondaryFrameworks
  );

  const handleFrameworksChange = (selectedFrameworks: SkillsFramework[]) => {
    setValue(FIELD_SKILLS_FRAMEWORK_IDS_KEY, map(selectedFrameworks, "id"), { shouldDirty: true });
    resetWarningEffect();
  };

  const handleSuggestionClick = (skillsFramework: SkillsFramework) => {
    setInitiallyPreselectedSkillsFramework(skillsFramework);
    setShowSidePanel(true);
  };

  return (
    <FormSection
      id={id}
      title={title}
      actions={
        <SkillsFrameworksSectionActions
          initiallyPreselectedSkillsFramework={initiallyPreselectedSkillsFramework}
          isConfigMatchFilterOn={isConfigMatchFilterOn}
          isSkillsFrameworksSuggestionsFetching={isSkillsFrameworksSuggestionsFetching}
          onFrameworksChange={handleFrameworksChange}
          primarySkillsFrameworksSuggestions={primarySkillsFrameworksSuggestions}
          secondarySkillsFrameworksSuggestions={secondarySkillsFrameworksSuggestions}
          setInitiallyPreselectedSkillsFramework={setInitiallyPreselectedSkillsFramework}
          setIsConfigMatchFilterOn={setIsConfigMatchFilterOn}
          setShowSidePanel={setShowSidePanel}
          showSidePanel={showSidePanel}
        />
      }
      qaId="ActivityForm.skillsFrameworks"
    >
      <Stack gap="spacingLg" center={!hasSkillsFrameworks}>
        {isSkillsFrameworksLoading && <LoadingDots circlesEnabled circleSize="xs" centered />}
        {!isSkillsFrameworksLoading && hasSkillsFrameworks && <SkillsFrameworksList />}
        {showEmptyState && <SkillsFrameworksEmptyState />}
      </Stack>
      <SkillsFrameworksSuggestionsBox
        isDisabled={showSidePanel || hasSkillsFrameworks}
        isFetching={isSkillsFrameworksSuggestionsFetching}
        onSuggestionClick={handleSuggestionClick}
        primaryFrameworksSuggestions={primaryFrameworksSuggestions}
        secondaryFrameworksSuggestions={secondaryFrameworksSuggestions}
      />
    </FormSection>
  );
};
